import { EllipsisVertical, LogOut, UserCircle } from "lucide-react";
import { FC, useState } from "react";

import { m } from "@recobra/i18n/messages";
import { Avatar, AvatarFallback, AvatarImage } from "@recobra/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@recobra/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@recobra/ui/sidebar";

import { ProfileSheet } from "../profile";

export const SidebarNavUser: FC<{
  userName: string;
  userEmail?: string;
  userAvatar?: string;
  onSignOut: () => void;
}> = ({ userName, userEmail, userAvatar, onSignOut }) => {
  const { isMobile } = useSidebar();
  const initials = userInitials(userName);
  const [profileOpen, setProfileOpen] = useState(false);

  return (
    <>
      <ProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger render={<SidebarMenuButton size="lg" tooltip={userName} />}>
              <UserAvatarImage name={userName} avatarUrl={userAvatar} initials={initials} />
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{userName}</span>
                {userEmail ? (
                  <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                ) : null}
              </div>
              <EllipsisVertical className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-56"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuGroup>
                <DropdownMenuLabel>
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <UserAvatarImage name={userName} avatarUrl={userAvatar} initials={initials} />
                    <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{userName}</span>
                      {userEmail ? (
                        <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                      ) : null}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setProfileOpen(true)}>
                  <UserCircle />
                  {m.nav_profile()}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={onSignOut}>
                  <LogOut />
                  {m.nav_sign_out()}
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
};

const userInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return m.common_unknown();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();

  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
};

const UserAvatarImage: FC<{
  name: string;
  avatarUrl?: string;
  initials: string;
}> = ({ name, avatarUrl, initials }) => (
  <Avatar>
    {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
    <AvatarFallback>{initials !== m.common_unknown() ? initials : <UserCircle />}</AvatarFallback>
  </Avatar>
);
